import React, { useRef, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const InvoicePreviewDialog = ({ open, onClose, html, documentTitle = 'document' }) => {
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState('297mm');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open && iframeRef.current) {
      const iframe = iframeRef.current;

      const setupIframe = () => {
        try {
          if (iframe.contentWindow && iframe.contentWindow.document.body) {
            const doc = iframe.contentWindow.document;

            // Size iframe to its actual content so the outer container scrolls fully
            const body = doc.body;
            const docEl = doc.documentElement;
            const contentHeight = Math.max(
              body.scrollHeight, body.offsetHeight,
              docEl.clientHeight, docEl.scrollHeight, docEl.offsetHeight
            );
            setIframeHeight(contentHeight > 100 ? `${contentHeight + 24}px` : '297mm');
          }
        } catch (error) {
          setIframeHeight('297mm');
        }
      };

      iframe.addEventListener('load', setupIframe);
      const timer = setTimeout(setupIframe, 200);

      return () => {
        iframe.removeEventListener('load', setupIframe);
        clearTimeout(timer);
      };
    }
  }, [open, html]);

  const handlePrint = () => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      try {
        const doc = iframe.contentWindow.document;
        // Inject print-color-adjust so browsers preserve all background colors/images
        if (!doc.getElementById('_pca_fix')) {
          const s = doc.createElement('style');
          s.id = '_pca_fix';
          s.textContent = [
            '@media print {',
            '  * {',
            '    -webkit-print-color-adjust: exact !important;',
            '    print-color-adjust: exact !important;',
            '    color-adjust: exact !important;',
            '  }',
            '  .invoice-container, .page {',
            '    padding-bottom: 20px !important;',
            '    margin-bottom: 0 !important;',
            '  }',
            '  .footer {',
            '    padding-bottom: 10px !important;',
            '    margin-bottom: 0 !important;',
            '  }',
            '}',
          ].join('\n');
          doc.head.appendChild(s);
        }
      } catch (e) { /* cross-origin guard */ }
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const handleDownload = async () => {
    let tempDiv = null;
    try {
      setDownloading(true);

      // Render source HTML off-screen at exact A4 pixel width so canvas is
      // exactly one A4 page tall (no overflow => no extra blank page in PDF).
      tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-99999px';
      tempDiv.style.top = '0';
      tempDiv.style.width = '794px';   // A4 width at 96 DPI
      tempDiv.style.height = '1123px'; // A4 height at 96 DPI
      tempDiv.style.background = '#ffffff';
      tempDiv.style.overflow = 'hidden';
      document.body.appendChild(tempDiv);

      // Force the download layout: flex column so footer sits at the very bottom.
      // Targets both .page (some templates) and .invoice-container (NL/FR/EN invoice templates).
      const containerEl = tempDiv.querySelector('.page') || tempDiv.querySelector('.invoice-container');
      if (containerEl) {
        containerEl.style.height = '1123px';
        containerEl.style.minHeight = '1123px';
        containerEl.style.display = 'flex';
        containerEl.style.flexDirection = 'column';
        containerEl.style.overflow = 'hidden';
        containerEl.style.paddingBottom = '20px';
        containerEl.style.marginBottom = '0';
        const cw = containerEl.querySelector('.content-wrapper');
        if (cw) {
          cw.style.flex = '1 1 auto';
          cw.style.minHeight = '0';
        }
        const ftr = containerEl.querySelector('.footer');
        if (ftr) {
          ftr.style.marginTop = 'auto';
          ftr.style.paddingBottom = '10px';
          ftr.style.marginBottom = '0';
        }
      }

      // Wait for images/fonts to settle
      await new Promise((r) => setTimeout(r, 120));

      const canvas = await html2canvas(tempDiv, {
        scale: 1.5,  // Reduced from 2 to 1.5 for smaller file size while maintaining good quality
        useCORS: true,
        logging: false,
        width: 794,
        height: 1123,
        windowWidth: 794,
        windowHeight: 1123,
        backgroundColor: '#ffffff',
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();   // 210mm
      const pdfHeight = pdf.internal.pageSize.getHeight(); // 297mm
      const imgData = canvas.toDataURL('image/jpeg', 0.88);  // Reduced from 0.98 to 0.88 for optimal size/quality balance

      // Single page — image sized exactly to A4
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);

      pdf.save(
        `${documentTitle.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.pdf`
      );
    } catch (error) {
    } finally {
      if (tempDiv && tempDiv.parentNode) {
        tempDiv.parentNode.removeChild(tempDiv);
      }
      setDownloading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '960px',
          maxWidth: '95vw',
          height: '95vh',
          maxHeight: '95vh',
          m: 2,
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ 
        position: 'relative', 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: '#f5f5f7'
      }}>
        {/* Header bar */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1.5,
          bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DescriptionIcon />
            <Typography variant="subtitle1" fontWeight={600}>
              {documentTitle}
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose}
            size="small"
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Preview area (horizontal scroll hidden, vertical only when needed) */}
        <Box sx={{ 
          flex: 1, 
          overflowX: 'hidden',
          overflowY: 'auto', 
          bgcolor: '#525659',
          display: 'flex',
          justifyContent: 'center',
          p: 3
        }}>
          <Box sx={{ 
            width: '210mm',
            maxWidth: '100%',
            bgcolor: 'white',
            boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
            borderRadius: '2px',
          }}>
            <iframe
              ref={iframeRef}
              srcDoc={html}
              title={documentTitle}
              scrolling="no"
              style={{ 
                width: '100%',
                height: iframeHeight,
                border: 'none', 
                display: 'block',
              }}
            />
          </Box>
        </Box>
        
        {/* Action bar */}
        <DialogActions sx={{ 
          p: 2, 
          gap: 1, 
          justifyContent: 'flex-end', 
          borderTop: '1px solid #e0e0e0',
          bgcolor: 'white'
        }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={downloading}
            sx={{ 
              textTransform: 'none',
              borderRadius: '6px',
              borderColor: '#667eea',
              color: '#667eea',
              px: 2.5,
              '&:hover': {
                borderColor: '#764ba2',
                bgcolor: 'rgba(102, 126, 234, 0.04)'
              }
            }}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={downloading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DownloadIcon />}
            onClick={handleDownload}
            disabled={downloading}
            sx={{ 
              textTransform: 'none',
              borderRadius: '6px',
              px: 2.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 2px 6px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
                boxShadow: '0 4px 10px rgba(102, 126, 234, 0.5)'
              }
            }}
          >
            {downloading ? 'Generating...' : 'Download PDF'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default InvoicePreviewDialog;